import { api } from '../../../api/client';
import { fetchModuleRows } from '../../modules/queries';

/** A Maintenance Queue list row (the shared module list `instm-maint-queue`). */
export interface QueueRow {
  oid: string;
  maintenanceId: string;
  dueDate: string | null;
  instrumentName: string;
  frequency: string;
  tasks: string;
  taskCount: number;
  daysPastDue: number;
  ownership: string;
  status: string;
}

export interface RecordHeader {
  oid: string;
  maintenanceId: string | null;
  instrumentName: string | null;
  taskDescription: string | null;
  frequency: string | null;
  nextMaintainDate: string | null;
  taskOwnership: string | null;
  serviceCompanyId: string | null;
  servicedBy: string | null;
  skip: boolean;
  skipReasonId: string | null;
  status: string | null;
  maintainedDate: string | null;
  maintainedBy: string | null;
}

export interface RecordTask {
  oid: string;
  dueDate: string | null;
  taskId: string | null;
  category: string | null;
  taskDescription: string | null;
  actionDescription: string | null;
  comment: string | null;
  completedDate: string | null;
  completedBy: string | null;
}

export interface RecordAttachment {
  oid: string;
  fileName: string | null;
  categoryId: string | null;
  category: string | null;
  /** The dbo.FileData Oid behind the Attachment link. */
  fileId: string | null;
  attachment: string | null;
  date: string | null;
  operator: string | null;
}

export interface Option { id: string; name: string }

export interface RecordView {
  header: RecordHeader;
  tasks: RecordTask[];
  attachments: RecordAttachment[];
  serviceCompanies: Option[];
  skipReasons: Option[];
  employees: Option[];
}

export interface RecordRowInput { oid: string; selected: boolean; actionDescription: string | null; comment: string | null }

export interface RecordInput {
  openedOid: string;
  serviceCompanyId: string | null;
  servicedBy: string | null;
  skip: boolean;
  skipReasonId: string | null;
  rows: RecordRowInput[];
}

export interface SubmitResult { updated: number; outcomes: string[]; nextOids: string[] }

/** Every row of the queue (it is a short list: one per instrument and frequency). */
export async function fetchQueue(): Promise<QueueRow[]> {
  const res = await fetchModuleRows('instm-maint-queue', { page: 1, pageSize: 5000, search: '', rangeDays: null, filters: {} });
  return (res.data ?? []) as unknown as QueueRow[];
}

export async function fetchRecord(oid: string, onlyOids?: string[]): Promise<RecordView> {
  const { data } = await api.get<{ data: RecordView }>(`/api/v1/maintenance-queue/${oid}`,
    { params: onlyOids?.length ? { oids: onlyOids.join(',') } : undefined });
  return data.data;
}

export async function saveRecord(oid: string, input: RecordInput): Promise<void> {
  await api.put(`/api/v1/maintenance-queue/${oid}`, input);
}

export async function submitRecord(oid: string, input: RecordInput): Promise<SubmitResult> {
  const { data } = await api.post<{ data: SubmitResult }>(`/api/v1/maintenance-queue/${oid}/submit`, input);
  return data.data;
}

// ── Maintenance Log ────────────────────────────────────────────────────────────────────────────────────────────────────

/** A Maintenance Log row — one maintenance setup that has been maintained (the shared module list `instm-maint-log`). */
export interface LogSetupRow {
  oid: string;
  instrumentId: string;
  instrumentName: string;
  department: string;
  category: string;
  lastMaintenanceDate: string | null;
  nextMaintenanceDate: string | null;
}

/** One finished occurrence in a setup's Maintained Log. */
export interface LogEntry {
  oid: string;
  maintenanceId: string | null;
  instrumentId: string | null;
  instrumentName: string | null;
  frequency: string | null;
  tasks: string | null;
  ownership: string | null;
  maintainedDate: string | null;
  maintainedBy: string | null;
  status: string | null;
}

export interface DoneHeader {
  oid: string;
  maintenanceId: string | null;
  instrumentName: string | null;
  taskDescription: string | null;
  frequency: string | null;
  nextMaintainDate: string | null;
  taskOwnership: string | null;
  serviceCompany: string | null;
  skip: boolean;
  skipReason: string | null;
  status: string | null;
  maintainedDate: string | null;
  maintainedBy: string | null;
}

export interface DoneView { header: DoneHeader; tasks: RecordTask[]; attachments: RecordAttachment[] }

export async function fetchLogSetups(): Promise<LogSetupRow[]> {
  const res = await fetchModuleRows('instm-maint-log', { page: 1, pageSize: 5000, search: '', rangeDays: null, filters: {} });
  return (res.data ?? []) as unknown as LogSetupRow[];
}

export async function fetchLog(setupOid: string, months: number | null): Promise<LogEntry[]> {
  const { data } = await api.get<{ data: LogEntry[] }>(`/api/v1/maintenance-queue/log/${setupOid}`,
    { params: months ? { months } : undefined });
  return data.data;
}

export async function fetchDone(oid: string): Promise<DoneView> {
  const { data } = await api.get<{ data: DoneView }>(`/api/v1/maintenance-queue/done/${oid}`);
  return data.data;
}

export async function rollbackRecord(oid: string, reason: string): Promise<number> {
  const { data } = await api.post<{ data: { count: number } }>(`/api/v1/maintenance-queue/done/${oid}/rollback`, { reason });
  return data.data.count;
}

// ── Attachment tab (immediate saves; each call returns the task's attachments as they now stand) ─────────────────────

export interface AttachmentInput { fileName: string; categoryId: string | null; fileId: string | null }

export async function fetchAttachmentCategories(): Promise<Option[]> {
  const { data } = await api.get<{ data: Option[] }>('/api/v1/maintenance-queue/attachment-categories');
  return data.data;
}

/** Uploads the file first (the row then saves the returned FileData Oid), the two-step every file field here uses. */
export async function uploadAttachmentFile(file: File): Promise<string> {
  const fd = new FormData();
  fd.append('file', file);
  const { data } = await api.post<{ data: { oid: string } }>('/api/v1/maintenance-queue/attachments/file/upload', fd);
  return data.data.oid;
}

export async function addAttachment(taskOid: string, input: AttachmentInput): Promise<RecordAttachment[]> {
  const { data } = await api.post<{ data: RecordAttachment[] }>(`/api/v1/maintenance-queue/${taskOid}/attachments`, input);
  return data.data;
}

export async function editAttachment(attachmentOid: string, input: AttachmentInput): Promise<RecordAttachment[]> {
  const { data } = await api.put<{ data: RecordAttachment[] }>(`/api/v1/maintenance-queue/attachments/${attachmentOid}`, input);
  return data.data;
}

export async function deleteAttachment(attachmentOid: string): Promise<RecordAttachment[]> {
  const { data } = await api.delete<{ data: RecordAttachment[] }>(`/api/v1/maintenance-queue/attachments/${attachmentOid}`);
  return data.data;
}

/** A file link cannot be a plain href: the API wants the bearer token, so the file is fetched and handed over as a blob. */
export async function downloadAttachment(fileId: string, fileName: string | null): Promise<void> {
  const res = await api.get<Blob>(`/api/v1/maintenance-queue/attachments/file/${fileId}/download`, { responseType: 'blob' });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName ?? 'attachment';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
